<template>
  <WizardContainer
    width="792px"
    height="624px"
  >
    <WizardContent>
      <h4 class="my-6">
        Sign In To Your Account
      </h4>
      <div class="social-signup-btns">
        <DpplGoogleLinkedinButton
          :href="googleUrl"
          class="google-linkedin-button mb-4"
        >
          <img
            src="@/assets/icon-google.svg"
            width="64px"
            height="20px"
            alt="Authenticate with Google"
          >
        </DpplGoogleLinkedinButton>
        <DpplGoogleLinkedinButton
          :href="microsoftUrl"
          class="google-linkedin-button ml-4"
        >
          <img
            class="ml-1 mr-3"
            src="@/assets/icon-microsoft.svg"
            alt="Sign in with Microsoft"
          >
          Sign-in with Microsoft
        </DpplGoogleLinkedinButton>
      </div>
      <SsoLoginButton
        class="mb-6 ml-4 w-100"
        :is-active="singleSignOnMode"
        @toggle-sso="singleSignOnMode = !singleSignOnMode"
      />
      <DpplPageBreak
        text="OR"
        class="mb-9"
      />
      <v-form
        ref="form"
        v-model="valid"
        class="form"
        @submit.prevent="submitForm"
      >
        <!-- data-testids below are used by Vanta/Uptrend transactions -->
        <DpplTextField
          v-model="email"
          label="Company Email"
          :rules="emailRules"
          validate-on="blur"
          data-testid="login-email-input"
          @change="trimEmail"
        />
        <DpplTextField
          v-if="!singleSignOnMode"
          v-model="password"
          label="Password"
          :rules="passwordRules"
          type="password"
          autocomplete="off"
          data-testid="login-password-input"
          validate-on="blur"
          data-sentry-mask
        />
        <DpplSignupFormButton
          :loading="loading"
          type="submit"
          data-testid="login-submit-button"
          class="mb-4 mt-3"
        >
          Continue
        </DpplSignupFormButton>
      </v-form>
      <WizardFooter>
        <h5>
          <router-link
            class="mr-2"
            to="signup"
          >
            Create an Account
          </router-link>
          <span style="cursor: default;">-</span>
          <router-link
            class="ml-2"
            to="password-reset"
          >
            Forgot Password
          </router-link>
        </h5>
      </WizardFooter>
    </WizardContent>
  </WizardContainer>
</template>

<script>
import WizardContainer from '@/components/shared/WizardContainer.vue';
import WizardContent from '@/components/shared/WizardContent.vue';
import WizardFooter from '@/components/shared/WizardFooter.vue';
import { login, signupBuildAuthUrls, signupSendPasswordResetEmail } from '@/api/signup';
import { redirectToSsoProvider } from '@/helpers/redirect';
import validation from '@/helpers/validation';
import DpplTextField from '@/components/datapeople/DpplTextField.vue';
import DpplSignupFormButton from '@/components/datapeople/DpplSignupFormButton.vue';
import DpplGoogleLinkedinButton from '@/components/datapeople/DpplGoogleLinkedinButton.vue';
import DpplPageBreak from '@/components/datapeople/DpplPageBreak.vue';
import SsoLoginButton from '@/components/signup/SsoLoginButton.vue';
import { mapActions } from 'pinia';
import { mixpanelIdentify } from '@/helpers/thirdParty/mixpanel';
import { isNotTestAccount } from '@/helpers/thirdParty/verifyTestAccount';
import { useWizard } from '@/stores/wizard/wizard';
import { useTracking } from '@/stores/tracking/tracking';
import { getLocalStorageObject } from '@/helpers/localStorage';
import { nextTick } from 'vue';

export default {
  name: 'LoginView',
  components: {
    SsoLoginButton,
    WizardContainer,
    WizardContent,
    WizardFooter,
    DpplTextField,
    DpplSignupFormButton,
    DpplGoogleLinkedinButton,
    DpplPageBreak,
  },
  emits: ['track-page-load'],
  data() {
    return {
      email: '',
      password: '',
      emailRules: validation.emailRules(),
      passwordRules: validation.optionalPasswordRules(),
      lastAttemptEmail: null,
      valid: false,
      loading: false,
      googleUrl: null,
      microsoftUrl: null,
      singleSignOnMode: false,
    };
  },
  head() {
    return {
      title: this.$route.meta.title || 'Datapeople',
      link: [{ rel: 'canonical', href: 'https://app.datapeople.io/' }],
      meta: [
        {
          name: 'description',
          property: 'og:description',
          content:
          'Log into Datapeople begin writing job descriptions that are thoughtful, concise,'
          + ' and welcoming to candidates from every background.',
        },
        {
          name: 'robots',
          content: 'max-snippet:-1, max-image-preview:large, max-video-preview:-1',
        },
        {
          property: 'og:locale',
          content: 'en_US',
        },
        {
          property: 'og:type',
          content: 'website',
        },
        {
          name: 'title',
          property: 'og:title',
          content: 'Log into your Account - Datapeople',
        },
        {
          property: 'og:url',
          content: 'https://app.datapeople.io/',
        },
        {
          property: 'og:site_name',
          content: 'Datapeople',
        },
        {
          property: 'og:image',
          content: 'https://static.tapwage.com/taprecruit/Datapeople-fea.jpg',
        },
        {
          property: 'og:image:secure_url',
          content: 'https://static.tapwage.com/taprecruit/Datapeople-fea.jpg',
        },
        {
          property: 'og:image:width',
          content: '1200',
        },
        {
          property: 'og:image:height',
          content: '630',
        },
        {
          name: 'twitter:card',
          content: 'summary_large_image',
        },
        {
          name: 'twitter:description',
          content:
          'Log into Datapeople begin writing job descriptions that are thoughtful, concise,'
          + ' and welcoming to candidates from every background.',
        },
        {
          name: 'twitter:title',
          content: 'Log into your Account - Datapeople',
        },
        {
          name: 'twitter:site',
          content: '@Datapeopleio',
        },
        {
          name: 'twitter:image',
          content: 'https://static.tapwage.com/taprecruit/Datapeople-fea.jpg',
        },
        {
          name: 'twitter:creator',
          content: '@Datapeopleio',
        },
      ],
    };
  },
  async created() {
    const getLinkedinAndGoogleOauthUrls = async () => {
      const responseJSON = await signupBuildAuthUrls({ from: this.$route.query?.from });
      if (responseJSON.success === true) {
        this.googleUrl = responseJSON.google_auth_url;
        this.microsoftUrl = responseJSON.microsoft_auth_url;
      }
    };

    Promise.all([getLinkedinAndGoogleOauthUrls()]).catch((e) => {
      this.updateFlash({ message: e.message });
      this.loading = false;
    });
  },
  mounted() {
    this.$emit('track-page-load', { page: 'Sign In' });

    const { passwordChanged } = this.$route.query;

    if (passwordChanged === 'true') {
      this.updateFlash({ status: 'success', message: 'Password changed successfully! Please log in with your new password.' });

      this.$router.replace({
        name: this.$route.name,
        query: { ...this.$route.query, passwordChanged: undefined },
      });
    }
  },
  methods: {
    ...mapActions(useWizard, ['updateFlash']),
    async submitForm() {
      try {
        const formValidateResult = await this.$refs.form.validate();
        if (formValidateResult.valid) {
          this.loading = true;
          const responseJSON = await login({
            email: this.email,
            password: this.password,
          });

          if (responseJSON.success === true) {
            const {
              mixpanel, user,
            } = responseJSON;

            /* Identify user for tracking purposes after they have logged in */
            mixpanelIdentify({ mixpanel });

            /* We must call this helper function because the tracking call does not have access
            to authData yet and cannot verify if a user is a test account */
            if (isNotTestAccount(user.userId)) {
              useTracking().login();
            }

            const redirectRoute = getLocalStorageObject({ key: 'redirectRoute', remove: true });
            const { from = '' } = this.$route.query;

            /* handle login from Datapeople Anywhere using username and password */
            if (from === 'datapeople-anywhere') {
              this.$router.replace({ name: 'datapeople-anywhere-login-success' });
            /* handle helpscout restricted docs auth callback */
            } else if (from.startsWith('help')) {
              const articlePath = from.substring(4);
              const searchParams = new URLSearchParams();
              searchParams.append('returnTo', articlePath);
              window.location = `/vue/helpscout-restricted-docs-callback?${searchParams}`;
            } else if (redirectRoute) {
              this.$router.replace(redirectRoute).catch(() => {});
            } else {
              this.$router.replace({ name: 'root' }).catch(() => {});
            }
          }
        }
      } catch (e) {
        if (!e.message || e.message.charAt(0) !== '{') {
          this.updateFlash({ message: `Login error: ${e.message}` });
          throw e;
        }
        const responseJSON = JSON.parse(e.message);
        const { success, email, errors } = responseJSON;
        if (success === false) {
          if (email) {
            this.lastAttemptEmail = email;
          }
          if (errors && errors.cooldown && errors.cooldown.length > 0) {
            this.updateFlash({ template: 'exceeded-tries' });
            nextTick(() => {
              document
                .getElementById('resend')
                .addEventListener('click', this.requestPasswordResetEmail);
            });
          } else if (errors && errors.password) {
            this.updateFlash({ template: 'incorrect-email-or-password' });
            nextTick(() => {
              document
                .getElementById('resend')
                .addEventListener('click', this.requestPasswordResetEmail);
            });
            // do not throw in this case as it's expected behavior and we don't need sentry error
            return;
          } else if (errors && errors.sso_mandatory) {
            localStorage.setItem('lastSso', JSON.stringify({
              provider: errors.provider,
              name: errors.company_name,
              isGoogle: errors.is_google,
              isMicrosoft: errors.is_microsoft,
            }));

            const { from = '' } = this.$route.query;
            redirectToSsoProvider({
              from,
              provider: errors.provider,
              isGoogle: errors.is_google,
              isMicrosoft: errors.is_microsoft,
            });
            return;
          }
        } else {
          this.updateFlash({ message: 'Something went wrong' });
        }
        throw e;
      } finally {
        this.loading = false;
      }
    },
    async requestPasswordResetEmail() {
      try {
        const responseJSON = await signupSendPasswordResetEmail({
          email: this.lastAttemptEmail,
        });
        if (responseJSON.success === true) {
          this.updateFlash({ status: 'success', message: 'Password reset email sent!' });
        }
      } catch (e) {
        this.updateFlash({ message: e.message });
        throw e;
      } finally {
        this.loading = false;
      }
    },
    trimEmail() {
      this.email = validation.trimSpaces(this.email);
    },
  },
};
</script>

<style lang="less" scoped>
.form {
  width: 100%;
}

.social-signup-btns {
  display: flex;
  justify-content: center;
  width: 100%;
}

.sso-button:deep(.v-btn__content) {
  overflow: hidden;
  text-transform: initial;
  font-size: 16px;
}

.google-linkedin-button {
  text-transform: initial;
}
</style>
